import type { Meta, StoryObj } from '@storybook/angular';
import { QtyInputComponent } from '../../../app/content/layout/components/product/qty-input/qty-input.component';

const meta: Meta<QtyInputComponent> = {
  title: 'Atoms/Inputs/QuantityInput',
  component: QtyInputComponent,
  parameters: {
    docs: {
      description: {
        component: 'A quantity input component with increment/decrement buttons. Supports debouncing and different sizes.',
      },
    },
  },
  argTypes: {
    qty: {
      control: { type: 'number' },
      description: 'The initial quantity value',
    },
    disable: {
      control: { type: 'boolean' },
      description: 'Whether the input is disabled',
    },
    small: {
      control: { type: 'boolean' },
      description: 'Whether to use the small variant',
    },
    enableDebounce: {
      control: { type: 'boolean' },
      description: 'Whether to enable debounced updates',
    },
    qtyChange: {
      action: 'qtyChange',
      description: 'Emitted when quantity changes',
    },
    submitEvent: {
      action: 'submitEvent',
      description: 'Emitted when Enter is pressed',
    },
  },
  args: {
    qty: 1,
    disable: false,
    small: false,
    enableDebounce: false,
  },
};

export default meta;
type Story = StoryObj<QtyInputComponent>;

export const Default: Story = {
  args: {
    qty: 1,
  },
  render: (args) => ({
    props: args,
    template: `<app-qty-input 
      [qty]="qty" 
      [disable]="disable" 
      [small]="small" 
      [enableDebounce]="enableDebounce"
      (qtyChange)="qtyChange($event)"
      (submitEvent)="submitEvent($event)">
    </app-qty-input>`,
  }),
};

export const Small: Story = {
  args: {
    qty: 5,
    small: true,
  },
  render: (args) => ({
    props: args,
    template: `<app-qty-input 
      [qty]="qty" 
      [disable]="disable" 
      [small]="small" 
      [enableDebounce]="enableDebounce"
      (qtyChange)="qtyChange($event)"
      (submitEvent)="submitEvent($event)">
    </app-qty-input>`,
  }),
};

export const Disabled: Story = {
  args: {
    qty: 3,
    disable: true,
  },
  render: (args) => ({
    props: args,
    template: `<app-qty-input 
      [qty]="qty" 
      [disable]="disable" 
      [small]="small" 
      [enableDebounce]="enableDebounce"
      (qtyChange)="qtyChange($event)"
      (submitEvent)="submitEvent($event)">
    </app-qty-input>`,
  }),
};

export const WithDebounce: Story = {
  args: {
    qty: 2,
    enableDebounce: true,
  },
  render: (args) => ({
    props: args,
    template: `<app-qty-input 
      [qty]="qty" 
      [disable]="disable" 
      [small]="small" 
      [enableDebounce]="enableDebounce"
      (qtyChange)="qtyChange($event)"
      (submitEvent)="submitEvent($event)">
    </app-qty-input>`,
  }),
};

export const AllVariants: Story = {
  render: () => ({
    template: `
      <div style="display: flex; flex-direction: column; gap: 2rem; max-width: 400px;">
        <div>
          <h4>Default</h4>
          <app-qty-input [qty]="1" (qtyChange)="qtyChange($event)"></app-qty-input>
        </div>
        <div>
          <h4>Small</h4>
          <app-qty-input [qty]="5" [small]="true" (qtyChange)="qtyChange($event)"></app-qty-input>
        </div>
        <div>
          <h4>Disabled</h4>
          <app-qty-input [qty]="3" [disable]="true" (qtyChange)="qtyChange($event)"></app-qty-input>
        </div>
        <div>
          <h4>With Debounce</h4>
          <app-qty-input [qty]="2" [enableDebounce]="true" (qtyChange)="qtyChange($event)"></app-qty-input>
        </div>
      </div>
    `,
    props: {
      qtyChange: (value: number) => console.log('Quantity changed:', value),
    },
  }),
  parameters: {
    docs: {
      description: {
        story: 'A showcase of all available quantity input variants.',
      },
    },
  },
};
